import { useState, useEffect } from 'react'
import CustomerAddressFields from '../../components/CustomerAddressFields'
import { emptyAddressDraft, isAddressDraftBlank, validateAddressDraft, addressDraftToRow, type AddressDraft } from '../../lib/customerAddress'
import { supabase } from '../../api/supabase'
import { useSyncToSage, type SageDepartmentCategory, type SageLedgerAccount, type SageTaxRate } from '../../hooks/useSyncToSage'
import { deriveVatFromEori, isValidUkEoriFormat } from '../../lib/cds/eori'
import ExistingCompaniesNotice from '../../components/ExistingCompaniesNotice'
import { findExistingCompanies, type ExistingCompany } from '../../lib/existingCompanies'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'
import CountrySelect from '../../components/CountrySelect'
import { countryFieldsForSave, countryForForm, countryNotValidMessage } from '../../lib/customerCountry'

export interface Customer {
  id?: string
  code: string
  name: string
  // Deprecated -- superseded by customer_addresses (address_type='operations').
  // Still read here (Detail.tsx Company Information fallback via
  // formatCustomerAddress) but no longer written by this form.
  address?: string
  country?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  payment_terms?: string
  currency: string
  credit_limit?: number | null
  customer_type?: string
  industry_type?: string
  sales_area?: string
  is_on_hold: boolean
  branch?: string
  is_active: boolean
  eori?: string
  vat_no?: string
  dan?: string
  use_cyuk_dan_default?: boolean
  rep_type_imports?: string
  rep_type_exports?: string
  vat_registered?: boolean
  postponed_vat_accounting?: boolean
  // Z75/Z75.1: per-customer Sage sync defaults, used by sync-to-sage only when a Bulk Import CSV
  // row leaves the corresponding cell blank -- see supabase/functions/_shared/sage.ts
  // (getCustomerSageDefaults). Stores human-readable code/name strings (matching CSV values),
  // not raw Sage ids. default_pva and default_currency are NOT separate fields here -- they're
  // postponed_vat_accounting and currency above, already serving that purpose.
  default_sales_ledger_code?: string
  default_purchase_ledger_code?: string
  default_department_code?: string
  default_tax_rate_id?: string
}

// CDS Names & Addresses v1.7 field-length limits (SHORTNAME/TID/TURN/DAN).
const CDS_LIMITS = { code: 18, eori: 18, vat_no: 12, dan: 35 } as const
// name is NOT capped/blocked -- customers.name intentionally stores the full
// untruncated company name (up to 200 chars). NAME_CDS_WARN is only the
// threshold for a non-blocking informational note; CDS's 35-char LONGNAME
// limit is actually applied at export time (see truncateLongnameForCds in
// src/lib/cds/addressFormatter.ts).
const NAME_CDS_WARN = 35
const REP_TYPES = ['', 'D', 'I', 'S'] as const
const REP_TYPE_LABELS: Record<string, string> = { '': '—', D: 'Direct', I: 'Indirect', S: 'Self' }

interface AddressType {
  id: string
  code: string
  description: string
  sort_order: number
}

// Z73.12: shape returned by the hmrc-eori-check edge function (both the live-HMRC path
// and the 24h cache-hit path return this same shape).
interface EoriCheckResult {
  success: boolean
  valid?: boolean
  hmrc_name?: string | null
  hmrc_address?: string | null
  checked_at?: string
  name_match?: boolean | null
  cached?: boolean
  down?: boolean
  error?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  customer?: Customer | null
  // `created` = a new company was just inserted (vs. an edit): the caller sends the user to that company's page.
  onSaved: (result: { id: string; created: boolean; addressError?: string }) => void
}

const CURRENCIES = ['GBP', 'USD', 'EUR', 'CNY', 'HKD']

const emptyForm: Customer = {
  code: '',
  name: '',
  address: '',
  country: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  payment_terms: '',
  currency: 'GBP',
  credit_limit: null,
  customer_type: '',
  industry_type: '',
  sales_area: '',
  is_on_hold: false,
  branch: 'BIRMINGHAM',
  is_active: true,
  eori: '',
  vat_no: '',
  dan: '',
  use_cyuk_dan_default: false,
  rep_type_imports: '',
  rep_type_exports: '',
  vat_registered: false,
  postponed_vat_accounting: false,
  default_sales_ledger_code: '',
  default_purchase_ledger_code: '',
  default_department_code: '',
  default_tax_rate_id: '',
}

export default function CustomerModal({ isOpen, onClose, customer, onSaved }: Props) {
  const [form, setForm] = useState<Customer>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A NEW company is only created after the database has been asked whether one like it already exists.
  const [existing, setExisting] = useState<ExistingCompany[] | null>(null)
  const [skipExistingCheck, setSkipExistingCheck] = useState(false)
  const [addressTypes, setAddressTypes] = useState<AddressType[]>([])
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<string>>(new Set())
  // First address, typed while creating a company (new companies only). Saved to customer_addresses -- never the legacy columns.
  const [addr, setAddr] = useState<AddressDraft>(emptyAddressDraft())
  const { fetchDepartmentCategories, fetchLedgerAccounts, fetchTaxRates } = useSyncToSage()
  const [ledgerAccounts, setLedgerAccounts] = useState<SageLedgerAccount[]>([])
  const [taxRates, setTaxRates] = useState<SageTaxRate[]>([])
  const [departmentCategories, setDepartmentCategories] = useState<SageDepartmentCategory[]>([])
  const [eoriCheck, setEoriCheck] = useState<EoriCheckResult | null>(null)
  const [checkingEori, setCheckingEori] = useState(false)

  useEffect(() => {
    supabase.from('address_types').select('id,code,description,sort_order').eq('is_active', true).order('sort_order')
      .then(({ data }) => setAddressTypes(data || []))
    // Z75: live from Sage -- empty arrays (dropdowns just don't render) if Sage isn't connected
    // or has none set up, same non-blocking convention as SageBulkImport's Department dropdown.
    fetchLedgerAccounts().then(setLedgerAccounts)
    fetchTaxRates().then(setTaxRates)
    fetchDepartmentCategories().then(setDepartmentCategories)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (customer) {
      setForm({ ...emptyForm, ...customer, country: countryForForm(customer.country) })
      if (customer.id) {
        supabase.from('customer_address_types').select('address_type_id').eq('customer_id', customer.id)
          .then(({ data }) => setSelectedTypeIds(new Set((data || []).map(r => r.address_type_id))))
      }
    } else {
      setForm(emptyForm)
      setSelectedTypeIds(new Set())
    }
    setAddr(emptyAddressDraft())
    setError(null)
    setEoriCheck(null)
  }, [customer, isOpen])

  if (!isOpen) return null

  const isEdit = !!customer?.id
  // Typing a different name/code starts the existing-company check afresh.
  useEffect(() => { setSkipExistingCheck(false); setExisting(null) }, [form.name, form.code])

  const set = (field: keyof Customer, value: unknown) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const toggleType = (id: string) =>
    setSelectedTypeIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  // Z73.12: live HMRC check -- force=true on the "Re-check" click bypasses the edge
  // function's 24h cache; a plain "Verify EORI" click reuses a fresh cached result if any.
  const verifyEori = async (force = false) => {
    if (!form.eori) return
    setCheckingEori(true)
    try {
      const { data, error } = await supabase.functions.invoke('hmrc-eori-check', {
        body: { eori: form.eori, customer_name: form.name, source: 'customer_modal', force },
      })
      if (error) throw error
      setEoriCheck(data as EoriCheckResult)
    } catch (err: any) {
      setEoriCheck({ success: false, down: true, error: err?.message || 'HMRC EORI service unavailable -- you can proceed but validation deferred.' })
    } finally {
      setCheckingEori(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim() || !form.name.trim()) {
      setError('Company Code and Name are required.')
      return
    }
    if (form.code.trim().length > CDS_LIMITS.code) {
      setError(`Company Code exceeds ${CDS_LIMITS.code} characters (CDS limit).`)
      return
    }
    if ((form.eori ?? '').length > CDS_LIMITS.eori) {
      setError(`EORI exceeds ${CDS_LIMITS.eori} characters (CDS limit).`)
      return
    }
    if ((form.vat_no ?? '').length > CDS_LIMITS.vat_no) {
      setError(`VAT No exceeds ${CDS_LIMITS.vat_no} characters (CDS limit).`)
      return
    }
    if ((form.dan ?? '').length > CDS_LIMITS.dan) {
      setError(`DAN exceeds ${CDS_LIMITS.dan} characters (CDS limit).`)
      return
    }
    // Country: one picker, both columns (country + country_code) written from it. Text that is not a country is refused, never guessed.
    const countryFields = countryFieldsForSave(form.country)
    if (!countryFields.ok) { setError(countryNotValidMessage(countryFields.text)); return }
    // Validate the address BEFORE creating the company, so a bad address can't leave a half-made company behind.
    const addrFilled = !isEdit && !isAddressDraftBlank(addr)
    if (addrFilled) {
      const addrErr = validateAddressDraft(addr)
      if (addrErr) { setError(addrErr); return }
    }
    setSaving(true)
    setError(null)
    try {
      if (!isEdit && !skipExistingCheck) {
        const found = await findExistingCompanies(form.name, form.code)
        if (found.length > 0) { setExisting(found); setSaving(false); return }
      }
      let customerId = customer?.id

      // Explicitly list only DB columns — prevents runtime bleed of extra fields
      // like address_types (present when customer prop is CustomerWithTypes).
      // address is intentionally omitted -- deprecated, superseded by
      // customer_addresses (see the Addresses card on the Company detail page).
      const payload = {
        code:           form.code.trim(),
        name:           form.name.trim(),
        country:        countryFields.country,
        country_code:   countryFields.country_code,
        contact_name:   form.contact_name   || null,
        contact_email:  form.contact_email  || null,
        contact_phone:  form.contact_phone  || null,
        payment_terms:  form.payment_terms  || null,
        currency:       form.currency,
        credit_limit:   form.credit_limit   || null,
        customer_type:  form.customer_type  || null,
        industry_type:  form.industry_type  || null,
        sales_area:     form.sales_area     || null,
        is_on_hold:     form.is_on_hold,
        branch:         form.branch         || null,
        is_active:      form.is_active,
        eori:                      form.eori                      || null,
        vat_no:                    form.vat_no                    || null,
        dan:                       form.dan                       || null,
        use_cyuk_dan_default:      !!form.use_cyuk_dan_default,
        rep_type_imports:          form.rep_type_imports           || null,
        rep_type_exports:          form.rep_type_exports           || null,
        vat_registered:            form.vat_registered,
        postponed_vat_accounting:  form.postponed_vat_accounting,
        default_sales_ledger_code:    form.default_sales_ledger_code    || null,
        default_purchase_ledger_code: form.default_purchase_ledger_code || null,
        default_department_code:      form.default_department_code      || null,
        default_tax_rate_id:          form.default_tax_rate_id          || null,
      }
      if (isEdit) {
        const { error: err } = await supabase.from('customers').update(payload).eq('id', customerId!)
        if (err) throw err
      } else {
        const { data, error: err } = await supabase.from('customers').insert(payload).select('id').single()
        if (err) throw err
        customerId = data.id
      }

      // First address of a NEW company. The company already exists at this point, so a failure is reported (not swallowed) and
      // the user is sent to the company page to add it there, rather than retrying and creating the company twice.
      let addressError: string | undefined
      if (addrFilled) {
        const { error: addrInsErr } = await supabase.from('customer_addresses').insert(addressDraftToRow(addr, customerId!, true))
        if (addrInsErr) addressError = addrInsErr.message
      }

      // Sync junction table
      await supabase.from('customer_address_types').delete().eq('customer_id', customerId!)
      if (selectedTypeIds.size > 0) {
        await supabase.from('customer_address_types').insert(
          Array.from(selectedTypeIds).map(tid => ({ customer_id: customerId!, address_type_id: tid }))
        )
      }

      // Z58.14: best-effort auto-bind -- if Code looks like a BoxTop AccNum and there's no
      // boxtop_account_number yet, the edge function tentatively binds it and verifies against
      // GetCustomerDetails, rolling back silently if it doesn't resolve. Never blocks the save.
      // BoxTop sync is off (lib/boxtopSync.ts): this used to fire on EVERY company save, invisibly. Only runs when it is switched back on.
      if (BOXTOP_SYNC_ENABLED) {
        supabase.functions.invoke('sync-to-boxtrax', { body: { action: 'verify_code_bind', customer_id: customerId } })
          .catch(err => console.warn('[CustomerModal] verify_code_bind failed (non-blocking):', err))
      }

      onSaved({ id: customerId!, created: !isEdit, addressError })
      onClose()
    } catch (err: any) {
      setError(err?.message || err?.error_description || String(err) || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Company' : 'New Company'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          {existing && !isEdit && (
            <ExistingCompaniesNotice
              typedName={form.name.trim()}
              matches={existing}
              busy={saving}
              onCreateAnyway={() => { setExisting(null); setSkipExistingCheck(true); setError('Confirmed as a different company — press Save again to create it.') }}
            />
          )}

          {/* Core */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => set('code', e.target.value.toUpperCase())}
                  maxLength={CDS_LIMITS.code}
                  className={inputCls}
                  placeholder="e.g. CYUK001"
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Set Code to match your BoxTop AccNum (3-8 uppercase letters/digits) to auto-bind it.
                </p>
              </div>
              {/* Full width, own row -- half-width (paired with Code) visually
                  clipped long real names, e.g. "SHENZHEN YOUMEI ELECTRONIC
                  TECHNOLOGY CO., LTD" (46 chars) didn't fit a half-width input.
                  No maxLength -- full name is stored untruncated; CDS's
                  35-char LONGNAME limit is applied only at export time. */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  className={inputCls}
                  placeholder="Company name"
                />
                {form.name.length > NAME_CDS_WARN && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Exceeds {NAME_CDS_WARN}-char CDS limit ({form.name.length} chars)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country
                </label>
                <CountrySelect value={form.country} onChange={code => set('country', code)} invalid={!countryFieldsForSave(form.country).ok} />
                {!countryFieldsForSave(form.country).ok && (
                  <p className="mt-1 text-xs text-red-600">Saved value &quot;{form.country}&quot; is not a country: choose one from the list.</p>
                )}
              </div>
            </div>
          </div>

          {/* Address Types (multi-role) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Company Roles
            </h3>
            {addressTypes.length === 0 ? (
              <p className="text-sm text-gray-400">Loading roles…</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {addressTypes.map(at => {
                  const checked = selectedTypeIds.has(at.id)
                  return (
                    <button
                      key={at.id}
                      type="button"
                      onClick={() => toggleType(at.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        checked
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {at.description}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Contact
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
                <input type="text" value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input type="email" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                <input type="text" value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>

          {/* First address -- new companies only; existing companies edit addresses on their own page (Addresses card) */}
          {!isEdit && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Address
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">First address of this company (optional here; more can be added on the company page).</p>
              <CustomerAddressFields value={addr} onChange={patch => setAddr(p => ({ ...p, ...patch }))} showRoles />
            </div>
          )}

          {/* Customs / Compliance -- Task O: replaces the free-text Address
              field with the identity fields CDS declarations actually need. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Customs / Compliance
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EORI</label>
                <div className="flex gap-2">
                  <input type="text" value={form.eori || ''}
                    onChange={e => {
                      const eori = e.target.value.toUpperCase()
                      set('eori', eori)
                      setEoriCheck(null)
                      // Z73.5 addendum: UK EORI = GB + VAT number + 000 -- auto-suggest VAT No the
                      // moment a derivable EORI is entered, but only into an EMPTY field (never
                      // silently overwrite a value staff already typed/kept).
                      if (!form.vat_no) {
                        const derived = deriveVatFromEori(eori)
                        if (derived) set('vat_no', derived)
                      }
                    }}
                    maxLength={CDS_LIMITS.eori} className={inputCls} placeholder="GB123456789000" />
                  <button type="button" onClick={() => verifyEori(!!eoriCheck)} disabled={checkingEori || !form.eori}
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium disabled:opacity-50 whitespace-nowrap">
                    {checkingEori ? 'Checking…' : eoriCheck ? 'Re-check' : 'Verify EORI'}
                  </button>
                </div>
                {form.eori && !isValidUkEoriFormat(form.eori) && /^GB/i.test(form.eori) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Doesn't match the usual UK EORI shape (GB + 9-12 digit VAT no. + 000) -- double-check if this looks wrong.
                  </p>
                )}
                {eoriCheck && (
                  <div className="mt-2 p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-xs">
                    {eoriCheck.success === false ? (
                      <p className={eoriCheck.down ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}>
                        {eoriCheck.down ? '⚫' : '🔴'} {eoriCheck.error}
                      </p>
                    ) : !eoriCheck.valid ? (
                      <p className="text-red-600 dark:text-red-400">🔴 Not valid / not registered with HMRC</p>
                    ) : eoriCheck.name_match === false ? (
                      <div className="text-amber-600 dark:text-amber-400">
                        <p>🟡 Valid, but name mismatch -- HMRC has "{eoriCheck.hmrc_name}"</p>
                        {eoriCheck.hmrc_address && <p className="text-gray-500 dark:text-gray-400 mt-0.5">{eoriCheck.hmrc_address}</p>}
                      </div>
                    ) : (
                      <div className="text-green-600 dark:text-green-400">
                        <p>🟢 Valid{eoriCheck.hmrc_name ? ` -- matches "${eoriCheck.hmrc_name}"` : ' with HMRC'}</p>
                        {eoriCheck.hmrc_address && <p className="text-gray-500 dark:text-gray-400 mt-0.5">{eoriCheck.hmrc_address}</p>}
                      </div>
                    )}
                    {eoriCheck.checked_at && (
                      <p className="text-gray-400 dark:text-gray-500 mt-1">
                        Last checked: {new Date(eoriCheck.checked_at).toLocaleString()}{eoriCheck.cached ? ' (cached)' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">VAT No</label>
                <input type="text" value={form.vat_no || ''} onChange={e => set('vat_no', e.target.value.toUpperCase())}
                  maxLength={CDS_LIMITS.vat_no} className={inputCls} placeholder="GB123456789" />
                {(() => {
                  const derived = deriveVatFromEori(form.eori)
                  if (!derived || !form.vat_no || form.vat_no === derived) return null
                  return (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      ⚠ Doesn't match the VAT number implied by the EORI ({derived}) -- verify which is correct.
                    </p>
                  )
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">DAN</label>
                <input type="text" value={form.dan || ''} onChange={e => set('dan', e.target.value.toUpperCase())}
                  maxLength={CDS_LIMITS.dan} className={inputCls} placeholder="Deferment account no." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Duty paid via CY UK DAN</label>
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                  <input type="checkbox" checked={!!form.use_cyuk_dan_default} onChange={e => set('use_cyuk_dan_default', e.target.checked)} />
                  Default: pay this customer&apos;s duty/VAT from our deferment account
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">CY UK advances the money and re-bills (deferment fee on the quote). Pre-fills the eCustoms screen; can be overridden per declaration.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rep Type (Imports)</label>
                <select value={form.rep_type_imports || ''} onChange={e => set('rep_type_imports', e.target.value)} className={inputCls}>
                  {REP_TYPES.map(t => <option key={t} value={t}>{REP_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rep Type (Exports)</label>
                <select value={form.rep_type_exports || ''} onChange={e => set('rep_type_exports', e.target.value)} className={inputCls}>
                  {REP_TYPES.map(t => <option key={t} value={t}>{REP_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-6 mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.vat_registered ?? false} onChange={e => set('vat_registered', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">VAT Registered</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.postponed_vat_accounting ?? false} onChange={e => set('postponed_vat_accounting', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Postponed VAT Accounting</span>
              </label>
            </div>
          </div>

          {/* Finance */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Finance
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                <select value={form.currency} onChange={e => set('currency', e.target.value)} className={inputCls}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Credit Limit</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.credit_limit ?? ''}
                  onChange={e => set('credit_limit', e.target.value ? parseFloat(e.target.value) : null)}
                  className={inputCls} placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Terms</label>
                <input type="text" value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)} className={inputCls} placeholder="e.g. 30 days" />
              </div>
            </div>
          </div>

          {/* Z75/Z75.1: Sage sync defaults -- only used by sync-to-sage when a Bulk Import CSV
              row leaves the corresponding column blank; an explicit CSV value always wins. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Sage Sync Defaults
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              Used only when a Bulk Import CSV row leaves the matching column blank -- an explicit CSV value always overrides these.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Sales Ledger Code</label>
                <select value={form.default_sales_ledger_code || ''} onChange={e => set('default_sales_ledger_code', e.target.value)} className={inputCls}>
                  <option value="">— No default —</option>
                  {ledgerAccounts.filter(a => a.code).map(a => (
                    <option key={a.id} value={a.code!}>{a.code} — {a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Purchase Ledger Code</label>
                <select value={form.default_purchase_ledger_code || ''} onChange={e => set('default_purchase_ledger_code', e.target.value)} className={inputCls}>
                  <option value="">— No default —</option>
                  {ledgerAccounts.filter(a => a.code).map(a => (
                    <option key={a.id} value={a.code!}>{a.code} — {a.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Not yet consumed anywhere -- reserved for a future purchase-invoice sync.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Department</label>
                <select value={form.default_department_code || ''} onChange={e => set('default_department_code', e.target.value)} className={inputCls}>
                  <option value="">— No default —</option>
                  {departmentCategories.map(d => (
                    <option key={d.id} value={d.code}>{d.code} — {d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Tax Rate</label>
                <select value={form.default_tax_rate_id || ''} onChange={e => set('default_tax_rate_id', e.target.value)} className={inputCls}>
                  <option value="">— No default —</option>
                  {taxRates.map(r => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_on_hold} onChange={e => set('is_on_hold', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">On Hold</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium">
              {saving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
